// Tab admin "Fichas y Catálogos".
// - Genera la ficha técnica de un producto (abre /producto/:sku/ficha).
// - Arma un catálogo showcase (sin precios) con los productos seleccionados.
// - Completa/normaliza specs con IA (edge function enrich-product-specs) con
//   revisión antes de guardar (SpecReviewModal).

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { thumbUrl } from '../../../lib/imageUrl';
import { SpecReviewModal } from './SpecReviewModal';

type Row = {
  id: string;
  sku: string;
  brand_code: string;
  name: string;
  status: string;
  legacy_lutron_line: string | null;
  category_paths: string[] | null;
  has_variants: boolean | null;
  specs: Record<string, unknown> | null;
  specs_proposed: Record<string, unknown> | null;
  images: unknown;
};

const LUTRON_LINES = ['Caseta', 'RadioRA3', 'Maestro', 'Wallbox', 'Vive', 'HomeWorks QSX', 'Lumaris'];
const AI_BATCH = 15;

function hasSpecs(r: Row): boolean {
  return !!r.specs && typeof r.specs === 'object' && Object.keys(r.specs).length > 0;
}
function pendingReview(r: Row): boolean {
  return !!r.specs_proposed && typeof r.specs_proposed === 'object' && Object.keys(r.specs_proposed).length > 0;
}
function firstImg(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null;
  const arr = raw as { url?: string; is_thumbnail?: boolean }[];
  const t = arr.find(i => i.is_thumbnail);
  return (t?.url ?? arr[0]?.url) ?? null;
}

type Review = { id: string; sku: string; name: string };

export function AdminFichasCatalogos() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [brand, setBrand] = useState<'all' | 'nuled' | 'lutron'>('nuled');
  const [line, setLine] = useState<string>('all');
  const [soloRevisar, setSoloRevisar] = useState(false);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [titulo, setTitulo] = useState('Catálogo NULED');

  const [enriching, setEnriching] = useState<Record<string, boolean>>({});
  const [bulkRunning, setBulkRunning] = useState(false);
  const [banner, setBanner] = useState('');
  const [review, setReview] = useState<Review | null>(null);

  const load = () => {
    setLoading(true);
    (async () => {
      let query = supabase.from('products')
        .select('id, sku, brand_code, name, status, legacy_lutron_line, category_paths, has_variants, specs, specs_proposed, images')
        .order('name')
        .limit(3000);
      if (brand !== 'all') {
        if (brand === 'nuled') query = query.or('brand_code.eq.nuled,brand_code.is.null');
        else query = query.eq('brand_code', 'lutron');
      }
      if (line !== 'all') query = query.eq('legacy_lutron_line', line);
      const { data } = await query;
      setRows((data as Row[]) ?? []);
      setLoading(false);
    })();
  };

  useEffect(load, [brand, line]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = rows;
    if (needle) list = list.filter(r => r.name.toLowerCase().includes(needle) || r.sku.toLowerCase().includes(needle));
    if (soloRevisar) list = list.filter(pendingReview);
    return list;
  }, [rows, q, soloRevisar]);

  const selCount = Object.keys(selected).length;
  const reviewCount = useMemo(() => rows.filter(pendingReview).length, [rows]);
  const coverage = useMemo(() => {
    if (filtered.length === 0) return 0;
    return Math.round((filtered.filter(hasSpecs).length / filtered.length) * 100);
  }, [filtered]);

  const toggle = (r: Row) => setSelected(prev => {
    const next = { ...prev };
    if (next[r.id]) delete next[r.id]; else next[r.id] = r.sku;
    return next;
  });
  const selectAllVisible = () => setSelected(prev => {
    const next = { ...prev };
    for (const r of filtered) next[r.id] = r.sku;
    return next;
  });
  const clearSelection = () => setSelected({});

  const openFicha = (sku: string) => window.open(`/producto/${encodeURIComponent(sku)}/ficha`, '_blank');

  const buildCatalog = () => {
    const ids = Object.keys(selected);
    if (ids.length === 0) return;
    window.open(`/catalogo/pdf?ids=${ids.join(',')}&titulo=${encodeURIComponent(titulo || 'Catálogo NULED')}`, '_blank');
  };

  const enrichOne = async (r: Row) => {
    setBanner('');
    setEnriching(prev => ({ ...prev, [r.id]: true }));
    const { data, error } = await supabase.functions.invoke('enrich-product-specs', { body: { product_ids: [r.id] } });
    setEnriching(prev => { const n = { ...prev }; delete n[r.id]; return n; });
    if (error) { setBanner(`Error IA: ${error.message}`); return; }
    const res = ((data as { results?: { ok: boolean; error?: string }[] })?.results ?? [])[0];
    if (res && res.ok) {
      // refrescar el row para que el modal lea specs_proposed fresco
      const { data: fresh } = await supabase.from('products').select('specs_proposed').eq('id', r.id).maybeSingle();
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, specs_proposed: (fresh?.specs_proposed as Record<string, unknown>) ?? null } : x));
      setReview({ id: r.id, sku: r.sku, name: r.name });
    } else {
      setBanner(`IA no pudo con ${r.sku}: ${res?.error ?? 'sin resultado'}`);
    }
  };

  const enrichSelected = async () => {
    const ids = Object.keys(selected);
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBanner(`Completando ${ids.length} productos con IA…`);
    let okN = 0; let errN = 0;
    for (let i = 0; i < ids.length; i += AI_BATCH) {
      const chunk = ids.slice(i, i + AI_BATCH);
      const { data, error } = await supabase.functions.invoke('enrich-product-specs', { body: { product_ids: chunk } });
      if (error) { errN += chunk.length; continue; }
      const results = (data as { results?: { ok: boolean }[] })?.results ?? [];
      for (const r of results) { if (r.ok) okN++; else errN++; }
      setBanner(`Completando… ${Math.min(i + AI_BATCH, ids.length)}/${ids.length}`);
    }
    setBulkRunning(false);
    setBanner(`Listo: ${okN} con propuesta lista para revisar${errN ? `, ${errN} con error` : ''}. Activa "Por revisar" para aprobarlas.`);
    load();
  };

  return (
    <AdminContainer
      eyebrow="Catálogo"
      title="Fichas y Catálogos"
      action={
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={enrichSelected} disabled={selCount === 0 || bulkRunning} style={{
            background: (selCount === 0 || bulkRunning) ? theme.borderStrong : theme.accentDark,
            color: 'white', border: 'none', padding: '10px 18px', fontSize: 13, fontWeight: 700,
            cursor: (selCount === 0 || bulkRunning) ? 'default' : 'pointer', fontFamily: theme.font.sans, borderRadius: 2,
          }}>{bulkRunning ? 'Completando…' : `✨ Completar selección (${selCount})`}</button>
          <button onClick={buildCatalog} disabled={selCount === 0} style={{
            background: selCount === 0 ? theme.borderStrong : theme.brand,
            color: 'white', border: 'none', padding: '10px 20px', fontSize: 13, fontWeight: 700,
            cursor: selCount === 0 ? 'default' : 'pointer', fontFamily: theme.font.sans, borderRadius: 2,
          }}>📖 Armar catálogo ({selCount})</button>
          <button onClick={() => window.open(`/catalogo/pdf?brand=nuled&titulo=${encodeURIComponent(titulo || 'Catálogo NULED')}`, '_blank')} style={{
            background: theme.accentDark,
            color: 'white', border: 'none', padding: '10px 18px', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: theme.font.sans, borderRadius: 2,
          }}>📖 Catálogo completo (revista)</button>
        </div>
      }
    >
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o SKU…" style={{
          flex: '1 1 240px', padding: '9px 12px', border: `1px solid ${theme.border}`, borderRadius: 2, fontSize: 13, fontFamily: theme.font.sans,
        }} />
        <Select value={brand} onChange={v => { setBrand(v as 'all' | 'nuled' | 'lutron'); setLine('all'); }} options={[
          { value: 'all', label: 'Todas las marcas' }, { value: 'nuled', label: 'NULED' }, { value: 'lutron', label: 'Lutron' },
        ]} />
        {brand === 'lutron' && (
          <Select value={line} onChange={setLine} options={[
            { value: 'all', label: 'Todas las líneas' }, ...LUTRON_LINES.map(l => ({ value: l, label: l })),
          ]} />
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={soloRevisar} onChange={e => setSoloRevisar(e.target.checked)} />
          Por revisar {reviewCount > 0 && <span style={{ background: theme.warn, color: 'white', borderRadius: 999, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{reviewCount}</span>}
        </label>
      </div>

      {banner && (
        <div style={{ padding: '10px 14px', background: theme.accentSoft, border: `1px solid ${theme.accentDark}`, fontSize: 12, color: theme.brand, marginBottom: 12 }}>
          {banner}
        </div>
      )}

      {/* Barra de selección + cobertura */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', background: theme.bgSoft, border: `1px solid ${theme.border}`, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: theme.textMuted }}>{loading ? 'Cargando…' : `${filtered.length} productos`}</span>
        <span style={{ fontSize: 12, color: coverage >= 60 ? theme.ok : theme.warn, fontWeight: 600 }}>{coverage}% con especificaciones</span>
        <div style={{ flex: 1 }} />
        <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título del catálogo" style={{ padding: '7px 10px', border: `1px solid ${theme.border}`, borderRadius: 2, fontSize: 12, width: 180 }} />
        <button onClick={selectAllVisible} style={linkBtn}>Seleccionar todo</button>
        <button onClick={clearSelection} style={linkBtn}>Limpiar ({selCount})</button>
      </div>

      {/* Lista */}
      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 2, overflow: 'hidden' }}>
        {filtered.map((r, i) => {
          const isSel = !!selected[r.id];
          const img = firstImg(r.images);
          const isEnriching = !!enriching[r.id];
          const needsReview = pendingReview(r);
          return (
            <div key={r.id} style={{
              display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px',
              borderBottom: i === filtered.length - 1 ? 'none' : `1px solid ${theme.border}`,
              background: isSel ? theme.accentSoft : 'white',
            }}>
              <input type="checkbox" checked={isSel} onChange={() => toggle(r)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
              {img ? (
                <img src={thumbUrl(img, 80)} alt="" style={{ width: 38, height: 38, objectFit: 'contain', background: theme.bgImage, border: `1px solid ${theme.border}`, flexShrink: 0 }} />
              ) : (
                <div style={{ width: 38, height: 38, background: theme.bgImage, border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: theme.textMuted, flexShrink: 0 }}>💡</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.brand, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{r.sku}</span>
                  <Tag color={r.brand_code === 'lutron' ? theme.brandTag.lutron : theme.brandTag.nuled}>
                    {r.brand_code === 'lutron' ? (r.legacy_lutron_line ?? 'Lutron') : 'NULED'}
                  </Tag>
                  {r.has_variants && <Tag color={theme.textMuted}>variantes</Tag>}
                  {needsReview ? <Tag color={theme.warn}>por revisar ✨</Tag>
                    : hasSpecs(r) ? <Tag color={theme.ok}>specs ✓</Tag>
                    : <Tag color={theme.err}>sin specs</Tag>}
                </div>
              </div>
              {needsReview ? (
                <button onClick={() => setReview({ id: r.id, sku: r.sku, name: r.name })} style={{ ...outlineBtn, borderColor: theme.warn, color: theme.warn }}>👁 Revisar</button>
              ) : (
                <button onClick={() => enrichOne(r)} disabled={isEnriching} style={{ ...outlineBtn, borderColor: theme.accentDark, color: theme.accentDark, opacity: isEnriching ? 0.6 : 1 }}>
                  {isEnriching ? '…' : '✨ IA'}
                </button>
              )}
              <button onClick={() => openFicha(r.sku)} style={outlineBtn}>📄 Ficha</button>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>Sin resultados</div>
        )}
      </div>

      {review && (
        <SpecReviewModal
          productId={review.id}
          sku={review.sku}
          name={review.name}
          onClose={(saved) => {
            const done = review;
            setReview(null);
            if (saved && done) {
              supabase.from('products').select('specs, specs_proposed').eq('id', done.id).maybeSingle().then(({ data }) => {
                setRows(prev => prev.map(x => x.id === done.id ? {
                  ...x,
                  specs: (data?.specs as Record<string, unknown>) ?? x.specs,
                  specs_proposed: (data?.specs_proposed as Record<string, unknown>) ?? null,
                } : x));
              });
            }
          }}
        />
      )}
    </AdminContainer>
  );
}

const linkBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: theme.brand, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font.sans, padding: 0 };
const outlineBtn: React.CSSProperties = { background: 'transparent', color: theme.brand, border: `1px solid ${theme.brand}`, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans, borderRadius: 2, whiteSpace: 'nowrap' };

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: '9px 12px', border: `1px solid ${theme.border}`, borderRadius: 2, fontSize: 13, fontFamily: theme.font.sans, background: 'white', color: theme.text,
    }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '1px 7px', background: color, color: 'white', fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, borderRadius: 2 }}>
      {children}
    </span>
  );
}
